import type { Ref } from 'react';
import { createContext, useContext } from 'react';
import type { ButtonProps as AriaButtonProps } from 'react-aria-components/Button';
import { Button as AriaButton } from 'react-aria-components/Button';
import { composeRenderProps } from 'react-aria-components/composeRenderProps';
import type { ContextValue } from 'react-aria-components/slots';
import { Provider } from 'react-aria-components/slots';
import { TextContext } from 'react-aria-components/Text';
import { ClearPressResponder } from '@react-aria/interactions';
import type { VariantProps } from 'class-variance-authority';
import { cva } from 'class-variance-authority';

import { disabledTooltipTriggerProps, PerceivableContext, useIsDisabledTooltipTrigger } from './Perceivable';
import { ProgressBar } from './ProgressBar';
import { Text } from './Text';
import { useLPContextProps } from './utils';

import styles from './styles/Button.module.css';

const buttonStyles = cva(styles.base, {
	variants: {
		size: {
			small: styles.small,
			medium: styles.medium,
			large: styles.large,
		},
		variant: {
			default: styles.default,
			primary: styles.primary,
			destructive: styles.destructive,
			minimal: styles.minimal,
			picker: styles.picker,
		},
	},
	defaultVariants: {
		size: 'medium',
		variant: 'default',
	},
});

interface ButtonVariants extends VariantProps<typeof buttonStyles> {}
interface ButtonProps extends AriaButtonProps, ButtonVariants {
	ref?: Ref<HTMLButtonElement>;
	/**
	 * The action's intent. `primary` marks the single main action on a surface, `destructive` marks
	 * one that deletes or cannot be undone, `minimal` recedes for secondary or repeated actions, and
	 * `picker` is reserved for controls that open a menu or popover.
	 * @default 'default'
	 */
	variant?: ButtonVariants['variant'];
	/**
	 * Visual scale. `large` is for onboarding and empty-state calls to action, `small` for dense
	 * toolbars and table rows. A string child is wrapped in a `Text` of the matching size.
	 * @default 'medium'
	 */
	size?: ButtonVariants['size'];
}
interface ButtonContextValue extends ButtonProps {
	isPressed?: boolean;
}

const ButtonContext = createContext<ContextValue<ButtonContextValue, HTMLButtonElement>>(null);

/**
 * A button performs an action, with mouse, touch, and keyboard interactions.
 *
 * Reach for a different component when the control does something other than fire an action:
 * `LinkButton` navigates to an href, `IconButton` shows an icon with no visible label,
 * `ToggleButton` holds a pressed/unpressed state, and `Link` is inline text rather than a control.
 *
 * The label is the child, not a prop: write `<Button>Save</Button>`. There is no `label`, `text`, or
 * `children` content prop, and there won't be one — content belongs to the consumer's JSX.
 *
 * A string child is wrapped in a `Text` sized to match the button. Element children pass through
 * untouched. Mark a leading icon with `slot="start-icon"` and a trailing icon with `slot="end-icon"`. The
 * slotted element must be a direct child of the button, and a fragment counts as direct. Button
 * keeps it at that edge and owns the spacing around it. Set `isPending` to show an indeterminate
 * `ProgressBar` and dim the label while an action is in flight.
 *
 * Defaults injected through `ButtonContext` are overridden by props passed directly to the button.
 *
 * https://react-spectrum.adobe.com/react-aria/Button.html
 *
 * @example
 * ```tsx
 * <Button variant="primary" onPress={save}>Save changes</Button>
 * ```
 */
const Button = ({ ref, ...props }: ButtonProps) => {
	const [mergedProps, mergedRef] = useLPContextProps(props, ref, ButtonContext);
	const perceivableProps = useContext(PerceivableContext);
	const isDisabledTrigger = useIsDisabledTooltipTrigger(mergedProps.isDisabled);
	const { size = 'medium', variant = 'default' } = mergedProps;

	const button = (
		<AriaButton
			{...mergedProps}
			{...perceivableProps}
			{...(isDisabledTrigger && disabledTooltipTriggerProps)}
			ref={mergedRef}
			data-lp-variant={variant}
			className={composeRenderProps(mergedProps.className, (className, renderProps) =>
				buttonStyles({ ...renderProps, size, variant, className }),
			)}
		>
			{composeRenderProps(mergedProps.children, (children, { isPending }) => (
				<Provider values={[[TextContext, { className: isPending ? styles.pending : undefined }]]}>
					{isPending && <ProgressBar isIndeterminate aria-label="loading" className={styles.progress} />}
					{typeof children === 'string' ? (
						<Text size={size as 'small' | 'medium' | 'large'}>{children}</Text>
					) : (
						children
					)}
				</Provider>
			))}
		</AriaButton>
	);

	return isDisabledTrigger ? <ClearPressResponder>{button}</ClearPressResponder> : button;
};

export { Button, ButtonContext, buttonStyles };
export type { ButtonProps, ButtonVariants };
